import React, {useEffect, useState} from 'react';
import {useAppContext} from '../../../context/AppContext/AppContext';
import { isCallSwitch, isYay} from '../../../helpers';
import WhatsNewModal, {hasSwitchDeadlinePassed, isBannerPermanent} from '../../../modals/WhatsNewModal';
import csIcon from '../../../img/whats-new/callswitch/CS1.png';
import phdIcon from '../../../img/whats-new/pulsehd/PulseHD.png';
import yayIcon from '../../../img/whats-new/yay/YAY.png';
import styles from './styles.module.scss';

const SNOOZE_STORAGE_KEY = 'whats_new_snoozed_until';

const isSnoozed = (): boolean => {
    const snoozedUntil = Number(localStorage.getItem(SNOOZE_STORAGE_KEY));
    return !!snoozedUntil && Date.now() < snoozedUntil;
};

const getThemeLogo = (): string => {
    switch (true) {
        case isYay:
            return yayIcon;
        case isCallSwitch:
            return csIcon;
        default:
            return phdIcon;
    }
};

const WhatsNewBanner: React.FC = () => {
    const isWhatsNewEnabled = true;
    const {appName, newAppDownloadURL, appLogoAlt} = useAppContext();

    const [showBanner, setShowBanner] = useState<boolean>(() => !isSnoozed());
    const [showModal, setShowModal] = useState<boolean>(false);
    const [permanent, setPermanent] = useState<boolean>(() => isBannerPermanent());
    const [deadlinePassed, setDeadlinePassed] = useState<boolean>(() => hasSwitchDeadlinePassed());

    useEffect(() => {
        if (deadlinePassed) {
            return undefined;
        }

        const interval = window.setInterval(() => {
            setPermanent(isBannerPermanent());
            if (hasSwitchDeadlinePassed()) {
                setDeadlinePassed(true);
            }
        }, 60000);

        return () => window.clearInterval(interval);
    }, [deadlinePassed]);

    useEffect(() => {
        if (showBanner || permanent) {
            return undefined;
        }

        const snoozedUntil = Number(localStorage.getItem(SNOOZE_STORAGE_KEY));

        if (!snoozedUntil || Date.now() >= snoozedUntil) {
            setShowBanner(true);
            return undefined;
        }

        const timer = window.setTimeout(
            () => setShowBanner(true),
            snoozedUntil - Date.now()
        );

        return () => window.clearTimeout(timer);
    }, [showBanner, permanent]);

    if (!isWhatsNewEnabled || (!showBanner && !permanent)) {
        return null;
    }

    const handleDownload = () => {
        if (newAppDownloadURL) {
            window.open(newAppDownloadURL, '_blank');
        }
    };

    if (deadlinePassed) {
        return (
            <WhatsNewModal
                showModal
                setShowModal={() => undefined}
                appName={appName}
                forced
            />
        );
    }

    const snoozeUntilTomorrow = () => {
        const tomorrow = new Date();
        tomorrow.setHours(24, 0, 0, 0);
        localStorage.setItem(SNOOZE_STORAGE_KEY, String(tomorrow.getTime()));
        setShowModal(false);
        setShowBanner(false);
    };

    return (
        <>
            <div className={styles.banner}>
                <img src={getThemeLogo()} alt={appLogoAlt}/>
                <div className={styles.banner_text}>
                    <p>The redesigned {appName} desktop app is here</p>
                    <p>Discover a new interface, improved UX, and AI Call Insights</p>
                </div>
                <div className={styles.banner_actions}>
                    <button
                        type='button'
                        className={styles.whats_new_button}
                        onClick={() => setShowModal(true)}
                    >
                        See what&apos;s new
                    </button>
                    <button
                        type='button'
                        className={styles.download_button}
                        onClick={handleDownload}
                    >
                        Download now
                    </button>
                    {!permanent ? (
                        <button
                            type='button'
                            aria-label='Dismiss'
                            className={styles.close_button}
                            onClick={snoozeUntilTomorrow}
                        >
                            <i className='icon-cross'/>
                        </button>
                    ) : null}
                </div>
            </div>
            <WhatsNewModal
                showModal={showModal}
                setShowModal={setShowModal}
                appName={appName}
                onSecondaryClick={!permanent ? snoozeUntilTomorrow : undefined}
            />
        </>
    );
};

export default WhatsNewBanner;
